import { storageHelper } from '../../helpers/storage';
import { localstorage } from '../../helpers/storage/local.keys';
import { sessionstorage } from '../../helpers/storage/session.keys';

/**
 * Tenant (company / database) identity helpers — WA-3495.
 *
 * In production the active tenant is decided EXCLUSIVELY by the `custom:company`
 * claim of the Cognito idToken (`environment.production.alternativeGuid` is
 * `false`, so the client sends no `alternativeguid` header, and the backend's
 * `authorize()` ignores that header anyway). Those tokens live in cookies on
 * `.hssoft.com`, i.e. they are SHARED with `console.hssoft.com`. When support
 * switches the company in the console, the next token refresh silently
 * re-targets this already-open tab at a different database — with no reload and
 * no event of any kind.
 *
 * Everything this app cached for the previous tenant therefore has to be treated
 * as poison from that moment on: it belongs to another company's database.
 */

/**
 * Company GUIDs arrive in several shapes — the idToken claim uses dashes, the
 * backend's `companyId` uses underscores, and casing differs between the two —
 * so both sides of every comparison go through this.
 */
export const normalizeCompanyGuid = (guid?: string | null): string =>
  (guid ?? '').trim().toLowerCase().replace(/_/g, '-');

/** The tenant THIS TAB's cached state was built for ('' when not yet known). */
export const readStoredCompanyGuid = (): string =>
  normalizeCompanyGuid(storageHelper.session.getItem('activeCompanyGuid'));

export const writeStoredCompanyGuid = (guid: string): void => {
  storageHelper.session.setItem('activeCompanyGuid', normalizeCompanyGuid(guid));
};

/**
 * localStorage keys holding IDs of records that live in the TENANT database
 * (print forms, statistic profiles) rather than user-interface preferences.
 *
 * Column/sort models are deliberately NOT in this list: they are per-user layout
 * preferences that are meaningful in any company, and wiping them on every
 * support switch would be a visible regression for the user who owns the
 * browser. Only values that can dangle against another company's tables belong
 * here.
 */
const TENANT_SCOPED_LOCAL_KEYS: localstorage[] = [
  'payments.printFormId',
  'openDebts.previewId',
  'openDebts.sendingId',
  'orderStatistic.profiles',
  'orderStatistic.selectedProfileId',
  'productionStatistic.profiles',
  'productionStatistic.selectedProfileId',
];

/**
 * sessionStorage keys that are NOT tenant state and must survive the purge.
 *
 * `recordedLoginUserIds` dedupes LOGIN telemetry per browser session; it is a
 * property of the user's session, not of the company, so dropping it would
 * re-send a LOGIN event after every switch.
 *
 * Everything else is cleared, and the list is a deny-list ON PURPOSE: a
 * sessionStorage key added later defaults to "purged", which is the safe side.
 * The bug being fixed here exists precisely because tenant-scoped caches were
 * added over time with nothing responsible for invalidating them.
 */
const PRESERVED_SESSION_KEYS: sessionstorage[] = ['recordedLoginUserIds'];

/**
 * Drop every cache that is scoped to one company.
 *
 * sessionStorage is cleared wholesale — every key in `session.keys.ts` is either
 * tenant data (selected article/customer/order/invoice/print list, company
 * config, filters) or cheap to re-derive (`userAccessParams`), and the same
 * blanket clear is already the established reset primitive for a service-worker
 * takeover in `appInit.ts`. In-memory caches (the ~40 `configsData.dict*`
 * dictionaries behind the dropdowns — the WA-3581 cause) cannot be reached from
 * here at all, which is why callers pair this with a full page reload rather
 * than trying to reset state in place.
 */
export const purgeTenantScopedStorage = (): void => {
  const preserved = PRESERVED_SESSION_KEYS.map(
    (key) => [key, storageHelper.session.getItem(key)] as const,
  );

  sessionStorage.clear();
  TENANT_SCOPED_LOCAL_KEYS.forEach((key) => storageHelper.local.removeItem(key));

  preserved.forEach(([key, value]) => {
    if (value !== undefined && value !== null) storageHelper.session.setItem(key, value);
  });
};
